import { useEffect, FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBanners } from "../../actions/banners";
import { Box, Skeleton, SxProps, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppDispatch, RootState } from "@/store";
import { useAppDispatch } from "@/hooks";
import { useNavigate } from "react-router";


/**
 * Feature component
 * 
 * Responsible for rendering a dialog with a slider to edit percentage values related to the distribution of virtual battery percentages
 * 
 * - Delegate the rendering of percentage distribution to the `VirtualBatteryDistributedPercentageDetails` component.
 * 
 */

interface BannerProps {
  code: string;
  sx?: SxProps<Theme> | undefined;
  onContentChange?: (hasContent: boolean) => void;
}

const Banner: FC<BannerProps> = ({code, sx, onContentChange}) => {
  const dispatch = useAppDispatch();

  const banners = useSelector((state: RootState) => state.banners);
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const bannerData = banners.banners[code];
  const rawContent = bannerData?.content;
  const description = bannerData?.description || "";

  useEffect(() => {
    if (!banners.banners[code]) {
      dispatch(fetchBanners(code));
    }
  }, [code, dispatch, banners.banners, i18n.language]);

  useEffect(() => {
    onContentChange?.(!!rawContent && !banners.fetching[code]);
  }, [rawContent, banners.fetching[code], onContentChange]);

  const detectContentType = (base64String: string) => {
    let binaryString;
    try {
      binaryString = atob(base64String.slice(0, 30));
    } catch {
      return null;
    }
    // Decode a portion of the base64
    const signatures = {
      "image/png": "\x89PNG\r\n\x1a\n",
      "image/jpeg": "\xff\xd8\xff",
      "image/gif": "GIF87a",
      "image/webp": "RIFF"
    };

    for (const [type, signature] of Object.entries(signatures)) {
      if (binaryString.startsWith(signature)) {
        return type;
      }
    }
    
    return null;
  };

  let isClickable = false;
  let pathKey = "";
  let queryString = "";
  let altText = description || "Banner image";

  if (description.includes("?action=")) {
    isClickable = true;
    
    const [key, query] = description.split("?");
    pathKey = key;
    queryString = `?${query}`;

    altText = "Banner promocional"; 
  }

  const handleBannerClick = () => {
    if (isClickable && pathKey) {
      navigate(t(pathKey) + queryString);
    }
  };

  let renderedContent = null;

  if (rawContent) {
    const contentType = detectContentType(rawContent);
    if (contentType) {
      const imgSrc = `data:${contentType};base64,${rawContent}`;
      renderedContent = <img src={imgSrc} alt={description} />;
    } else {
      renderedContent = <div dangerouslySetInnerHTML={{ __html: rawContent }} />;
    }
  }
  
  return (
    <Box height="100%">
      {!banners.fetching[code] ? (
        renderedContent && (
          <Box
            sx={[
              { cursor: isClickable ? "pointer" : "default" },
              ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])
            ]}
            onClick={isClickable ? handleBannerClick : undefined}
          >
            {renderedContent}
          </Box>
        )
      ) : (
        <Skeleton variant="rectangular" animation="wave" width="100%" height="100%" />
      )}
    </Box>
  );
}

export default Banner;